import { Timestamp } from 'firebase/firestore';

/**
 * Avant-Projet — les informations recueillies au devis, portées par la
 * « Project Identity Sheet » (`docs/PM/avant-projet/`).
 *
 * Embarqué sur le document projet, PAS une entité séparée : le pipeline
 * `prospect → AP → projet` appartient au CRM. Les trois champs de jonction
 * (`apReference`, `quotationOwner`, `transferDate`) restent volontairement
 * plats — aucune contrainte, aucune unicité — pour être migrables sans
 * reprise de données le jour où le CRM prend la main.
 */

export const AP_FEASIBILITY = ['yes', 'no', 'with-reserves'] as const;
export type ApFeasibility = (typeof AP_FEASIBILITY)[number];

export const AP_SCOPES = ['innovate', 'design', 'industrialize', 'produce', 'remanufacture'] as const;
export type ApScope = (typeof AP_SCOPES)[number];

export const AP_TECHNOLOGIES = ['electronics', 'mechanics', 'embedded-sw', 'power'] as const;
export type ApTechnology = (typeof AP_TECHNOLOGIES)[number];

export const AP_CONNECTIVITY = ['ble', 'wifi', 'zigbee', 'thread', 'lora', 'cellular', 'satellite', 'other'] as const;
export type ApConnectivity = (typeof AP_CONNECTIVITY)[number];

export const AP_RISK_LEVELS = ['low', 'medium', 'high'] as const;
export type ApRiskLevel = (typeof AP_RISK_LEVELS)[number];

/** Les 5 jalons cibles du §02 — clés stables, indépendantes du vocabulaire PAD. */
export const AP_MILESTONE_KEYS = ['kickOff', 'designFreeze', 'engineeringRun', 'pilotRun', 'sop'] as const;
export type ApMilestoneKey = (typeof AP_MILESTONE_KEYS)[number];

export interface ApRisk {
  domain: string;
  level: ApRiskLevel;
  description?: string;
  mitigation?: string;
}

export interface AvantProjet {
  // §01 — contexte
  /** Référence du devis. Champ distinct : `Project.code` est verrouillé par /^P\d{4,5}$/. */
  apReference?: string;
  /** uid de l'auteur du devis. */
  quotationOwner?: string;
  clientContact?: string;
  market?: string;
  clientSpecRef?: string;
  productType?: string;
  feasibility?: ApFeasibility;
  needDescription?: string;
  altyorScope?: ApScope[];

  // §02 — cibles
  /** Dates promises au devis. Écrites à la création, JAMAIS réécrites ensuite. */
  baselineDates?: Partial<Record<ApMilestoneKey, Timestamp>>;
  targetProductCost?: number;
  targetSellPrice?: number;
  technologies?: ApTechnology[];
  connectivity?: ApConnectivity[];
  certifications?: string;
  targetMarkets?: string;
  volumes?: { year1?: number; year2?: number; year3?: number };

  // §04 — risques
  risks?: ApRisk[];

  /** Charnière avant-projet → projet. */
  transferDate?: Timestamp;
}
